// Dashboard filter strip — period filter that recomputes everything client-side.
//
// Pipeline on each filter change:
//   1. Toggle [data-filter-date] items visibility (hide cards out of range).
//   2. Rebuild each board column count + the "+ X autres" / empty placeholders.
//   3. Recompute every [data-kpi="<scope>.<key>"] from the JSON dataset
//      embedded in #dash-dataset.
//   4. Update the period pills shown next to each KPI section so the user
//      knows the numbers reflect the active period.

const KEY = "dash-filters";

const PERIOD_LABELS = {
  all:   "Totaux",
  week:  "7 derniers jours",
  month: "30 derniers jours",
  year:  "Cette année",
};

const readState = () => {
  try {
    const fromHash = new URLSearchParams(window.location.hash.slice(1));
    const stored = localStorage.getItem(KEY);
    const base = stored ? JSON.parse(stored) : {};
    return { period: fromHash.get("period") || base.period || "all" };
  } catch (_) {
    return { period: "all" };
  }
};

const writeState = (state) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
    const params = new URLSearchParams(window.location.hash.slice(1));
    params.set("period", state.period);
    history.replaceState(null, "", `#${params.toString()}`);
  } catch (_) { /* no-op */ }
};

const periodCutoff = (period) => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (period === "week")  start.setDate(start.getDate() - 7);
  if (period === "month") start.setDate(start.getDate() - 30);
  if (period === "year")  start.setMonth(0, 1);
  return start;
};

const inRange = (iso, start) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return false;
  return d >= start;
};

const matchesPeriod = (iso, period) => {
  if (!iso || period === "all") return true;
  return inRange(iso, periodCutoff(period));
};

// ---- DOM filtering ------------------------------------------------------
const filterItems = (period) => {
  document.querySelectorAll("[data-filter-date]").forEach((el) => {
    el.classList.toggle("is-filtered-out", !matchesPeriod(el.dataset.filterDate, period));
  });
};

const refreshBoard = (period) => {
  document.querySelectorAll("[data-board-column]").forEach((column) => {
    const items = column.querySelectorAll("[data-filter-item]");
    const visibleItems = column.querySelectorAll("[data-filter-item]:not(.is-filtered-out)");
    const visibleCount = visibleItems.length;
    const renderedCount = items.length;
    const totalCount = parseInt(column.querySelector("[data-board-count]")?.dataset.boardTotal || "0", 10);
    const offScreen = Math.max(0, totalCount - renderedCount);

    const display = period === "all" ? totalCount : visibleCount;
    const countEl = column.querySelector("[data-board-count]");
    if (countEl) countEl.textContent = display;

    const moreEl = column.querySelector("[data-board-more]");
    if (moreEl) moreEl.hidden = period !== "all" || offScreen <= 0;

    const emptyEl = column.querySelector("[data-board-empty]");
    if (emptyEl) emptyEl.hidden = visibleCount > 0 || period === "all";
  });
};

// ---- KPI math -----------------------------------------------------------
const SIGNED_STATUSES = new Set(["Signé", "Terminé"]);
const ACTIVE_STATUSES = new Set(["En négociation", "Signé", "Stand by"]);

const formatHumanCurrency = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")} M €`;
  if (n >= 1_000)     return `${Math.round(n / 1_000)} k €`;
  return `${n} €`;
};

const formatThousands = (n) => Number(n).toLocaleString("fr-FR").replace(/,/g, " ");

const computeKpis = (dataset, period) => {
  const start = periodCutoff(period);

  const filterByEtudeDate = (etude) => {
    if (period === "all") return true;
    // Choose the most relevant date for the etude
    const candidates = [etude.date_signature, etude.date_debut, etude.created_at].filter(Boolean);
    return candidates.some((d) => inRange(d, start));
  };

  const filterByCreated = (rec) => period === "all" || inRange(rec.created_at, start);

  const junior = dataset.junior || { etudes: [], contacts: [], adherents_count: 0 };
  const membre = dataset.membre || { etudes: [], phases: [], contacts: [] };

  const jEtudes = junior.etudes.filter(filterByEtudeDate);
  const jContacts = junior.contacts.filter(filterByCreated);

  const mEtudes = membre.etudes.filter(filterByEtudeDate);
  const mSigned = mEtudes.filter((e) => SIGNED_STATUSES.has(e.statut));
  const mCa = mSigned.reduce((sum, e) => sum + (e.ca || 0), 0);
  const mContacts = membre.contacts.filter(filterByCreated);

  // Phases: active during the period (date_debut <= period_end & date_fin >= period_start)
  // For "all" we keep the server-rendered count (currently-active phases).
  const phasesCount = period === "all"
    ? membre.phases.length
    : membre.phases.filter((p) => {
        if (!p.date_debut || !p.date_fin) return false;
        const debut = new Date(p.date_debut);
        const fin = new Date(p.date_fin);
        const periodEnd = new Date();
        return debut <= periodEnd && fin >= start;
      }).length;

  const jSignedInPeriod = jEtudes.filter((e) => SIGNED_STATUSES.has(e.statut));
  const jCa = jSignedInPeriod.reduce((sum, e) => sum + (e.ca || 0), 0);

  return {
    "junior.etudes_total":   jEtudes.length,
    "junior.etudes_actives": jEtudes.filter((e) => ACTIVE_STATUSES.has(e.statut)).length,
    "junior.ca_signe":       formatHumanCurrency(jCa),
    "junior.adherents":      junior.adherents_count, // not date-filtered
    "junior.contacts":       jContacts.length,

    "membre.etudes_total":    mEtudes.length,
    "membre.etudes_signees":  mSigned.length,
    "membre.ca_realise":      `${formatThousands(mCa)} €`,
    "membre.phases_en_cours": phasesCount,
    "membre.contacts":        mContacts.length,
  };
};

const refreshKpis = (dataset, period) => {
  const values = computeKpis(dataset, period);
  Object.entries(values).forEach(([key, value]) => {
    document.querySelectorAll(`[data-kpi="${key}"]`).forEach((el) => {
      el.textContent = value;
    });
  });

  // Update the section pills with the period label
  document.querySelectorAll("[data-period-pill]").forEach((el) => {
    el.textContent = PERIOD_LABELS[period] || PERIOD_LABELS.all;
  });
};

// ---- Init ---------------------------------------------------------------
const initDashFilters = () => {
  const root = document.querySelector(".dash-filters");
  if (!root || root.dataset.initialized === "true") return;
  root.dataset.initialized = "true";

  let dataset = { junior: { etudes: [], contacts: [], adherents_count: 0 }, membre: { etudes: [], phases: [], contacts: [] } };
  try {
    const node = document.getElementById("dash-dataset");
    if (node?.textContent) dataset = JSON.parse(node.textContent);
  } catch (e) {
    console.warn("Dashboard dataset failed to parse", e);
  }

  const state = readState();

  const sync = () => {
    root.querySelectorAll("[data-filter-period]").forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.filterPeriod === state.period);
    });
    filterItems(state.period);
    refreshBoard(state.period);
    refreshKpis(dataset, state.period);
  };

  root.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-filter-period]");
    if (!btn) return;
    state.period = btn.dataset.filterPeriod;
    writeState(state);
    sync();
  });

  sync();
};

export { initDashFilters };
